"use client";

import { badgeVariants } from "@/components/ui/badge";
import {
  Anchor,
  Clear,
  Content,
  Empty,
  Group,
  Input,
  Item,
  ItemIndicator,
  ItemText,
  Loading,
  Portal,
  Root,
  Separator,
  TagGroup,
  TagGroupItem,
  TagGroupItemRemove,
  Trigger,
} from "@/components/ui/combobox-primitive";
import {
  InputBase,
  InputBaseAdornmentButton,
  InputBaseControl,
  InputBaseFlexWrapper,
  InputBaseInput,
} from "@/components/ui/input-base";
import { cn } from "@/lib/utils";
import { Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { CheckIcon, ChevronsUpDownIcon, LoaderIcon, XIcon } from "lucide-react";

export const Combobox = Root;

function ComboboxInputBase({
  children,
  ...props
}: React.ComponentProps<typeof InputBase>) {
  return (
    <Anchor asChild>
      <InputBase data-slot="combobox-input-base" {...props}>
        {children}
        <Clear asChild>
          <InputBaseAdornmentButton>
            <XIcon />
          </InputBaseAdornmentButton>
        </Clear>
        <Trigger asChild>
          <InputBaseAdornmentButton>
            <ChevronsUpDownIcon />
          </InputBaseAdornmentButton>
        </Trigger>
      </InputBase>
    </Anchor>
  );
}

function ComboboxInput(props: React.ComponentProps<typeof Input>) {
  return (
    <ComboboxInputBase>
      <InputBaseControl>
        <Input asChild>
          <InputBaseInput data-slot="combobox-input" {...props} />
        </Input>
      </InputBaseControl>
    </ComboboxInputBase>
  );
}

function ComboboxTagsInput({
  children,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <ComboboxInputBase>
      <TagGroup asChild>
        <InputBaseFlexWrapper
          data-slot="combobox-tags-input"
          className="flex items-center gap-2"
        >
          {children}
          <InputBaseControl>
            <Input asChild>
              <InputBaseInput {...props} />
            </Input>
          </InputBaseControl>
        </InputBaseFlexWrapper>
      </TagGroup>
    </ComboboxInputBase>
  );
}

function ComboboxTag({
  children,
  className,
  ...props
}: React.ComponentProps<typeof TagGroupItem>) {
  return (
    <TagGroupItem
      data-slot="combobox-tag"
      className={cn(
        badgeVariants({ variant: "outline" }),
        "group gap-1 pr-1.5 wrap-break-word whitespace-normal data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <Slottable>{children}</Slottable>
      <TagGroupItemRemove className="group-data-disabled:pointer-events-none">
        <XIcon className="size-4" />
        <span className="sr-only">Remove</span>
      </TagGroupItemRemove>
    </TagGroupItem>
  );
}

function ComboboxContent({
  className,
  align = "center",
  alignOffset = 4,
  ...props
}: React.ComponentProps<typeof Content>) {
  return (
    <Portal>
      <Content
        data-slot="combobox-content"
        align={align}
        alignOffset={alignOffset}
        className={cn(
          "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 w-(--radix-popover-trigger-width) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        {...props}
      />
    </Portal>
  );
}

function ComboboxEmpty({
  className,
  ...props
}: React.ComponentProps<typeof Empty>) {
  return (
    <Empty
      data-slot="combobox-empty"
      className={cn("py-6 text-center text-sm", className)}
      {...props}
    />
  );
}

function ComboboxLoading({
  className,
  ...props
}: React.ComponentProps<typeof Loading>) {
  return (
    <Loading
      data-slot="combobox-loading"
      className={cn("flex items-center justify-center px-1.5 py-2", className)}
      {...props}
    >
      <LoaderIcon className="mask-conic-gradient-to-tr from-45deg size-4 animate-spin to-white" />
    </Loading>
  );
}

function ComboboxGroup({
  className,
  ...props
}: React.ComponentProps<typeof Group>) {
  return (
    <Group
      data-slot="combobox-group"
      className={cn(
        "**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-sm **:[[cmdk-group-heading]]:font-bold",
        className,
      )}
      {...props}
    />
  );
}

function ComboboxSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="combobox-separator"
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

export const comboboxItemStyle = cva(
  "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50",
);

type ComboboxItemProps = Omit<React.ComponentProps<typeof Item>, "children"> &
  Pick<React.ComponentProps<typeof ItemText>, "children">;

function ComboboxItem({ className, children, ...props }: ComboboxItemProps) {
  return (
    <Item
      data-slot="combobox-item"
      className={cn(comboboxItemStyle(), className)}
      {...props}
    >
      <ItemText>{children}</ItemText>
      <ItemIndicator className="absolute right-2 flex size-3.5 items-center justify-center">
        <CheckIcon className="size-4" />
      </ItemIndicator>
    </Item>
  );
}

export {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxInputBase,
  ComboboxItem,
  ComboboxLoading,
  ComboboxSeparator,
  ComboboxTag,
  ComboboxTagsInput,
};
